import { contrastingTextColor } from '@src/utility/colorContrast';
import { initialsFromName } from '@src/utility/initials';
import { parsePrimaryBrandColor } from '@src/utility/showcaseBranding';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import type { Showcase } from '../types.ts/story';

export type OrganizationAvatarProps = {
  album: Showcase;
  username: string | null | undefined;
  logoSize?: number;
  /** Compact mode: chip-only (no inline text), initials fallback, hover scale. */
  compact?: boolean;
  /** When set in compact mode and sidebar is expanded, shows email next to chip with smart truncation. */
  email?: string;
};

const splitEmail = (email: string) => {
  const atIdx = email.lastIndexOf('@');
  if (atIdx <= 0) return { local: email, domain: '' };
  return { local: email.slice(0, atIdx), domain: email.slice(atIdx) };
};

function OrganizationAvatar({
  album,
  username,
  logoSize = 34,
  compact = false,
  email,
}: OrganizationAvatarProps) {
  const [albumLogo, setAlbumLogo] = useState<string | undefined>(
    album?.logo?.url || album?.mainImage?.url,
  );

  // Reset on album change
  useEffect(() => {
    setAlbumLogo(album?.logo?.url || album?.mainImage?.url);
  }, [album.id]);

  const brandColor = parsePrimaryBrandColor(album?.branding?.primaryColors);
  const initials = initialsFromName(album?.title || username);
  const initialsBg = brandColor ?? 'var(--accent)';
  const initialsFg = brandColor
    ? contrastingTextColor(brandColor)
    : 'var(--gray-900)';
  // Show inline email only in compact (Option B) mode when sidebar is expanded.
  // logoSize ≥ 30 indicates the expanded chip (32px); collapsed chip is 28px.
  const showInlineEmail = compact && !!email && logoSize >= 30;
  const emailParts = email ? splitEmail(email) : null;

  return (
    <>
      <AvatarRing $compact={compact}>
        <OrganizationLogo size={logoSize} $compact={compact}>
          {albumLogo ? (
            <img
              src={`${
                albumLogo
              }?crop=focalpoint&fit=crop&fp-x=0.3&fp-y=0.6&w=${logoSize * 2}&h=${logoSize * 2}`}
              onError={() => {
                if (albumLogo !== album?.mainImage?.url) {
                  setAlbumLogo(album?.mainImage?.url);
                }
              }}
              alt={album.title}
            />
          ) : (
            <InitialsChip $bg={initialsBg} $fg={initialsFg} $size={logoSize}>
              {initials}
            </InitialsChip>
          )}
        </OrganizationLogo>
      </AvatarRing>
      {!compact && (
        <OrganizationInfo className="organization-info">
          <span className="album-title" title={album.title}>
            {album.title}
          </span>
          <span className="username" title={username ?? undefined}>
            {username}
          </span>
        </OrganizationInfo>
      )}
      {showInlineEmail && emailParts && (
        <CompactInfoBlock>
          <ShowcaseName title={album.title}>{album.title}</ShowcaseName>
          <EmailBlock title={email} aria-label={email}>
            <EmailLocal>{emailParts.local}</EmailLocal>
            <EmailDomain>{emailParts.domain}</EmailDomain>
          </EmailBlock>
        </CompactInfoBlock>
      )}
    </>
  );
}

OrganizationAvatar.displayName = 'OrganizationAvatar';

export default OrganizationAvatar;

export const OrganizationAvatarWrapper = styled.div`
  display: flex;
`;

const OrganizationInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  .album-title {
    color: var(--text-secondary);
    text-align: left;
    font-size: 16px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .username {
    color: var(--text-muted);
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    .username {
      font-size: 10px;
    }
    .album-title {
      font-size: 14px;
    }
  }
`;

const AvatarRing = styled.div<{ $compact?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  isolation: isolate;

  ${p =>
    p.$compact &&
    css`
      /* Glass ring extends 4px around the avatar chip; appears on
       * row hover. Same translucent-white + blur recipe as the
       * "See Details" pill on the recent stories card. */
      &::before {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.3);
        opacity: 0;
        transition: opacity var(--transition-normal) ease;
        pointer-events: none;
        z-index: -1;
      }

      [data-avatar-wrapper]:hover &::before {
        opacity: 1;
      }
    `}
`;

const OrganizationLogo = styled.div<{ size: number; $compact?: boolean }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  z-index: 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    width: min(${props => props.size}px, 30px);
    height: min(${props => props.size}px, 30px);
  }
`;

const CompactInfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
  gap: 2px;
`;

const ShowcaseName = styled.span`
  font-size: 12px;
  font-weight: var(--font-weight-medium, 500);
  line-height: 1.2;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmailBlock = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 11px;
  font-weight: var(--font-weight-regular, 400);
  line-height: 1.2;
  color: var(--text-muted);
`;

const EmailLocal = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmailDomain = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const InitialsChip = styled.div<{ $bg: string; $fg: string; $size: number }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  font-weight: var(--font-weight-semi-bold);
  font-size: ${p => Math.max(11, Math.round(p.$size * 0.4))}px;
  letter-spacing: 0.2px;
  user-select: none;
`;
